// The steps on a hearing recommendation after it is filed (db/migrations/4800,
// docs/DISCIPLINARY-RECOMMENDATIONS.md):
//   - Accept or reject. Accepting records what Fleetly adopts: the chair's
//     sanction and period, or lighter, with a reason for any difference.
//   - Amend, after release, with a reason. Never heavier than the chair.
//   - The warning letter, ready once the employee has signed for the
//     recommendation, sent through SignNow by a person pressing Send.
//
// Asked for by Luyanda on 6 October 2026 after DR-0001: the chair set six
// months and the page Samukelo Radebe signed said twelve. Every rule, ceiling
// and refusal is Postgres's; these panels only collect the answer and show the
// sentence that comes back.
import { useState } from 'react'
import { Check, Eye, FileText, Pencil, Send, X } from 'lucide-react'
import {
  useAmendDisciplinaryRecommendation, useDecideDisciplinaryRecommendation, useGetDisciplinaryLetterPreview,
  useOpenDisciplinarySignedCopy, useSendDisciplinaryWarningLetter,
} from '../../hooks/backend/disciplinary'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { Label } from '../../lib/shadcn/label'
import { NativeSelect } from '../../lib/shadcn/native-select'
import { Spinner } from '../../lib/shadcn/spinner'
import { Textarea } from '../../lib/shadcn/textarea'
import { toast } from '../../lib/shadcn/sonner'
import { Chip } from '../interviews/Chip'
import { writeErrorMessage } from '../../utils/writeError'
import { pdfBlobUrl } from '../../utils/loans'
import { day, isWarning, letterText, monthsParam, when, type Recommendation } from '../../utils/disciplinary'

type Sanction = { value: string; label: string }

async function attempt(setBusy: (v: string | null) => void, label: string, fn: () => Promise<unknown>, done: string | null, after: () => void) {
  setBusy(label)
  try {
    await fn()
    if (done) toast.success(done)
    after()
  } catch (err) {
    toast.error(writeErrorMessage(err))
  } finally {
    setBusy(null)
  }
}

function SanctionAndMonths({ id, sanctions, sanction, months, onSanction, onMonths }: {
  id: string; sanctions: Sanction[]; sanction: string; months: string
  onSanction: (v: string) => void; onMonths: (v: string) => void
}) {
  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
      <div className="space-y-1">
        <Label htmlFor={`${id}-s`}>Sanction</Label>
        <NativeSelect id={`${id}-s`} value={sanction} onChange={(e) => onSanction(e.target.value)}>
          {sanctions.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </NativeSelect>
      </div>
      {isWarning(sanction) && (
        <div className="space-y-1">
          <Label htmlFor={`${id}-m`}>Months on the record</Label>
          <Input id={`${id}-m`} type="number" min={1} max={24} value={months} onChange={(e) => onMonths(e.target.value)} />
        </div>
      )}
    </div>
  )
}

/** Accept or reject a filed recommendation. */
export function DecidePanel({ r, sanctions, onChanged, onClose }: { r: Recommendation; sanctions: Sanction[]; onChanged: () => void; onClose: () => void }) {
  const decide = useDecideDisciplinaryRecommendation<{ varied?: boolean }>()
  const [busy, setBusy] = useState<string | null>(null)
  const [mode, setMode] = useState<'accept' | 'reject'>('accept')
  const [sanction, setSanction] = useState(r.accepted_sanction ?? r.sanction)
  const [months, setMonths] = useState(String(r.accepted_months ?? r.recommended_months ?? ''))
  const [note, setNote] = useState(r.decision_note ?? '')
  const varied = sanction !== r.sanction || (isWarning(sanction) && Number(months || 0) !== Number(r.recommended_months ?? 0))

  return (
    <div className="space-y-2 rounded-md border border-border bg-muted/30 p-3 text-sm">
      <p className="text-xs text-muted-foreground">
        The chair recommended <span className="font-medium text-foreground">{r.recommended_text.toLowerCase()}</span>.
        {' '}Accepting is Fleetly adopting it: as written, or lighter with a reason. Nothing reaches {r.driver ?? 'the driver'} or the record until it is released.
      </p>
      <div className="flex gap-1 rounded-md bg-muted p-1">
        {(['accept', 'reject'] as const).map((m) => (
          <button key={m} type="button" onClick={() => setMode(m)}
            className={`flex-1 rounded-sm px-2 py-1 text-xs font-medium ${mode === m ? 'bg-background text-foreground shadow-retool-sm' : 'text-muted-foreground'}`}>
            {m === 'accept' ? 'Accept' : 'Reject'}
          </button>
        ))}
      </div>
      {mode === 'accept' && (
        <SanctionAndMonths id={`dec-${r.id}`} sanctions={sanctions} sanction={sanction} months={months} onSanction={setSanction} onMonths={setMonths} />
      )}
      <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)}
        placeholder={mode === 'reject'
          ? 'Why Fleetly is not adopting it. The chair and the driver may both ask.'
          : varied ? 'Why Fleetly is adopting something lighter than the chair recommended' : 'A note (optional)'} />
      <div className="flex gap-2">
        <Button size="sm" variant="brand" disabled={!!busy || (mode === 'reject' && !note.trim()) || (mode === 'accept' && varied && !note.trim())}
          onClick={() => void attempt(setBusy, 'decide', () => decide.trigger({
            id: r.id, decision: mode, sanction: mode === 'accept' ? sanction : null,
            months: mode === 'accept' && isWarning(sanction) ? monthsParam(months) : null, note: note || null,
          }).result, mode === 'accept' ? `${r.ref} accepted. Release it when you are ready.` : `${r.ref} rejected. Nothing goes on the record.`, () => { onClose(); onChanged() })}>
          {busy && <Spinner className="mr-1 h-3.5 w-3.5" />}
          {mode === 'accept' ? <><Check className="mr-1 h-3.5 w-3.5" /> {varied ? 'Accept, varied' : 'Accept as recommended'}</> : <><X className="mr-1 h-3.5 w-3.5" /> Reject it</>}
        </Button>
        <Button size="sm" variant="ghost" onClick={onClose}>Cancel</Button>
      </div>
    </div>
  )
}

/** Amend what was released. */
export function AmendPanel({ r, sanctions, onChanged, onClose }: { r: Recommendation; sanctions: Sanction[]; onChanged: () => void; onClose: () => void }) {
  const amend = useAmendDisciplinaryRecommendation<{ resent?: boolean; letter?: string | null; lapses_on?: string | null }>()
  const [busy, setBusy] = useState<string | null>(null)
  const [sanction, setSanction] = useState(r.accepted_sanction ?? r.sanction)
  const [months, setMonths] = useState(String(r.accepted_months ?? r.recommended_months ?? ''))
  const [charge, setCharge] = useState(r.charge)
  const [summary, setSummary] = useState(r.summary ?? '')
  const [reason, setReason] = useState('')

  return (
    <div className="space-y-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100">
      <p className="text-xs">
        Correct what was issued. The record keeps its issue date{r.issued_on ? ` (${day(r.issued_on)})` : ''} and takes the new sanction and lapse date.
        {r.status === 'sent' || r.status === 'manager_signed' ? ' The acknowledgement still out for signature is sent again with the corrected page.' : ''}
        {r.letter && r.letter.status && r.letter.status !== 'ready' ? ' The warning letter already sent is superseded, and a corrected one is made ready to send.' : ''}
        {' '}Never heavier than the chair recommended: {r.recommended_text.toLowerCase()}.
      </p>
      <SanctionAndMonths id={`am-${r.id}`} sanctions={sanctions} sanction={sanction} months={months} onSanction={setSanction} onMonths={setMonths} />
      <div className="space-y-1">
        <Label htmlFor={`am-c-${r.id}`}>What the hearing was about</Label>
        <Input id={`am-c-${r.id}`} value={charge} onChange={(e) => setCharge(e.target.value)} maxLength={600} />
      </div>
      <div className="space-y-1">
        <Label htmlFor={`am-f-${r.id}`}>The finding</Label>
        <Textarea id={`am-f-${r.id}`} rows={2} value={summary} onChange={(e) => setSummary(e.target.value)} maxLength={1200} />
      </div>
      <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)}
        placeholder="Why it is being amended. The employee may read this, for example: the chair set six months, not twelve." />
      <div className="flex gap-2">
        <Button size="sm" variant="brand" disabled={!!busy || !reason.trim() || !charge.trim()}
          onClick={() => void attempt(setBusy, 'amend', async () => {
            const x = await amend.trigger({
              id: r.id, sanction, months: isWarning(sanction) ? monthsParam(months) : null, charge,
              summary: summary.trim() ? summary : null, reason,
            }).result
            toast.success(`${r.ref} amended${x?.lapses_on ? `; it now lapses on ${day(x.lapses_on)}` : ''}.${x?.resent ? ' The acknowledgement is being sent again.' : ''}${x?.letter === 'ready' ? ' The corrected warning letter is ready to send.' : ''}`)
          }, null, () => { onClose(); onChanged() })}>
          {busy && <Spinner className="mr-1 h-3.5 w-3.5" />} <Pencil className="mr-1 h-3.5 w-3.5" /> Amend it
        </Button>
        <Button size="sm" variant="ghost" onClick={onClose}>Cancel</Button>
      </div>
    </div>
  )
}

/** The warning letter, once the employee has signed for the recommendation. */
export function LetterPanel({ r, onChanged }: { r: Recommendation; onChanged: () => void }) {
  const send = useSendDisciplinaryWarningLetter()
  const preview = useGetDisciplinaryLetterPreview<{ pdf_base64: string }>()
  const signed = useOpenDisciplinarySignedCopy<{ url: string }>()
  const [busy, setBusy] = useState<string | null>(null)
  const [confirming, setConfirming] = useState(false)
  const l = r.letter
  if (!l || !l.status) return null
  const t = letterText(l)
  // A failed send returns the letter to ready (disciplinary_rec_letter_record_send_failed_v1).
  const canSend = l.status === 'ready' || l.status === 'declined'

  return (
    <div className="space-y-2 rounded-md border border-[hsl(var(--brand))]/30 bg-[hsl(var(--brand-soft))]/40 p-3 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium text-foreground">The warning letter</span>
        <span className="text-xs text-muted-foreground">{l.ref}</span>
        <Chip tone={t.tone}>{t.text}</Chip>
      </div>
      <p className="text-xs text-muted-foreground">
        {r.accepted_text ?? r.recommended_text}{r.issued_on ? `, from ${day(r.issued_on)}` : ''}{r.lapses_on ? ` until ${day(r.lapses_on)}` : ''}.
        {l.status === 'ready' ? ` ${r.driver ?? 'The driver'} signed for the recommendation; this is the warning itself, for you to send. You sign first, then ${r.driver ?? 'the driver'} signs for receipt.` : ''}
        {l.sent_at ? ` Sent ${when(l.sent_at)}${l.issuer ? ` by ${l.issuer}` : ''}.` : ''}
        {l.manager_signed_at ? ` Issuer signed ${when(l.manager_signed_at)}.` : ''}
        {l.signed_at ? ` ${r.driver ?? 'Driver'} signed ${when(l.signed_at)}.` : ''}
      </p>
      {l.driver_comment && <p className="rounded border border-border bg-background p-2 text-xs"><span className="font-medium">The driver wrote: </span>{l.driver_comment}</p>}
      {l.send_error && <p className="text-xs text-rose-700 dark:text-rose-300">{l.send_error}</p>}
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" disabled={!!busy}
          onClick={() => void attempt(setBusy, 'preview', async () => {
            const x = await preview.trigger({ id: r.id }, { skipCache: true })
            if (!x?.pdf_base64) throw new Error('The letter could not be put together. Try again.')
            window.open(pdfBlobUrl(x.pdf_base64), '_blank', 'noopener')
          }, null, () => undefined)}>
          {busy === 'preview' ? <Spinner className="mr-1 h-3.5 w-3.5" /> : <Eye className="mr-1 h-3.5 w-3.5" />} Preview the letter
        </Button>
        {canSend && !confirming && (
          <Button size="sm" variant="brand" disabled={!!busy} onClick={() => setConfirming(true)}>
            <Send className="mr-1 h-3.5 w-3.5" /> {l.status === 'declined' ? 'Send again' : 'Send for signature'}
          </Button>
        )}
        {l.has_envelope && (
          <Button size="sm" variant="outline" disabled={!!busy}
            onClick={() => void attempt(setBusy, 'signed', async () => {
              const x = await signed.trigger({ id: r.id, which: 'letter' }).result
              if (x?.url) window.open(x.url, '_blank', 'noopener')
            }, null, () => undefined)}>
            <FileText className="mr-1 h-3.5 w-3.5" /> {l.status === 'signed' ? 'The signed letter' : 'The letter in SignNow'}
          </Button>
        )}
      </div>
      {confirming && (
        <div className="space-y-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-amber-950 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100">
          <p>SignNow emails you first to sign as the person issuing it, then {r.driver ?? 'the driver'} to sign for receipt. Preview it first if you have not.</p>
          <div className="flex gap-2">
            <Button size="sm" variant="brand" disabled={!!busy}
              onClick={() => void attempt(setBusy, 'send', () => send.trigger({ id: r.id }).result,
                'Sending. Refresh in a minute to see it out for signature.', () => { setConfirming(false); onChanged() })}>
              {busy === 'send' && <Spinner className="mr-1 h-3.5 w-3.5" />} Send it
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setConfirming(false)}>Not yet</Button>
          </div>
        </div>
      )}
    </div>
  )
}

export function AmendmentHistory({ r }: { r: Recommendation }) {
  if (!r.amendments?.length) return null
  return (
    <ul className="space-y-1 border-l-2 border-amber-300 pl-3 text-xs text-muted-foreground">
      {r.amendments.map((a, n) => (
        <li key={n}>
          <span className="font-medium text-foreground">Amended {when(a.at)} by {a.by ?? 'unknown'}</span>
          {a.from !== a.to ? `: from ${a.from.toLowerCase()} to ${a.to.toLowerCase()}` : ''}. {a.reason}
        </li>
      ))}
    </ul>
  )
}
